import axios, { AxiosRequestConfig } from 'axios';

import { topchefConfiguration } from './topchef-configuration.js'
import { Authentication, DateRange, FollowedLead, Lead, LeadCreation, Place, PlaceDashboard, PublicLead } from './topchef.types.js'
import { apiRequest, apiSharedConfig, formatDateToIsoString, getErrorMessage } from './topchef.utils.js'


const authenticate = (): Promise<Authentication> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/authenticate/me`);


const getLoggedInPlace = (): Promise<Place> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/me`);


const updatePlace = (place: Place): Promise<Place> =>
  apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/places/${place.id}`, {...place});


const shareLead = (placeId: string, leadCreation: LeadCreation): Promise<Lead> =>
  apiRequest('POST', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/shared`, {...leadCreation});

const extractPublicLeadWithAI = (placeId: string, leadDescription: string): Promise<LeadCreation> => {
  return apiRequest('POST', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/shared/extract-with-ai`, {leadDescription});
}


const loadSharedLead = (placeId: string, leadId: string): Promise<Lead> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/shared/${leadId}`);


const loadPublicLeads = (placeId: string): Promise<PublicLead[]> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/public-leads`);


const loadAllAvailableLeads = (placeId: string): Promise<PublicLead[]> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/public-leads?onlyAvailableLeads=true`);

const loadAvailableLeadsMatchingMyCriteria = (placeId: string): Promise<PublicLead[]> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/public-leads-for-me`);


const loadSharedLeads = (placeId: string, limit: number | null): Promise<PublicLead[]> => {
  let url = `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/shared`;
  if (limit !== null && limit > 0) {
    url = `${url}?limit=${limit}`;
  }
  return apiRequest('GET', url);
}


const loadPublicLead = (placeId: string, leadId: string): Promise<PublicLead> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/public-leads/${leadId}`);


const loadFollowedLead = (placeId: string, leadId: string): Promise<FollowedLead> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/followed/${leadId}`);


const followLead = (placeId: string, leadId: string): Promise<Lead> =>
  apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/followed/${leadId}`, {});


const unfollowLead = (placeId: string, leadId: string): Promise<void> =>
  apiRequest('DELETE', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/followed/${leadId}`);


const signLead = (placeId: string, leadId: string): Promise<void> =>
  apiRequest('PUT', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/followed/${leadId}/signed`, {});


const loadFollowedLeads = (placeId: string, limit: number | null ): Promise<FollowedLead[]> => {
  let url = `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/followed`;
  if (limit !== null && limit > 0) {
    url = `${url}?limit=${limit}`;
  }
  return apiRequest('GET', url);
}


const loadLeadsToClose = (placeId: string, limit: number | null ): Promise<Lead[]> => {
  let url = `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/to-close`;
  if (limit !== null && limit > 0) {
    url = `${url}?limit=${limit}`;
  }
  return apiRequest('GET', url);
}


const loadLeadToClose = (placeId: string, leadId: string ): Promise<Lead> =>
  apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/my-leads/to-close/${leadId}`);

const loadDashboard = (placeId: string, currentRange: DateRange): Promise<PlaceDashboard> => {
  const from = formatDateToIsoString(currentRange.startDate!);
  const to = formatDateToIsoString(currentRange.endDate!);
  return apiRequest('GET', `${topchefConfiguration.apiUrl}/api/places/${placeId}/dashboard?startDate=${from}&endDate=${to}`);
}
  


async function shareBill(placeId: string, leadId: string, bill: File, amount: number): Promise<void> {

  const formData = new FormData();
  formData.append('bill', bill, bill?.name);
  formData.append('amount', String(amount));

  const requestConfig: AxiosRequestConfig = {
    ...apiSharedConfig,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  return await axios.put(topchefConfiguration.apiUrl + `/api/places/${placeId}/my-leads/to-close/${leadId}/billed`, formData, requestConfig)
  .then((response) => {
      return response.data;
  })
  .catch( (error) => {
    const errorMessage:string = getErrorMessage(error);
    return Promise.reject(new Error(errorMessage));
  });
}
  
const features = {
  authenticate,
  getLoggedInPlace,
  updatePlace,
  shareLead,
  loadSharedLeads,
  loadSharedLead,
  loadPublicLeads,
  loadAllAvailableLeads,
  loadAvailableLeadsMatchingMyCriteria,
  loadPublicLead,
  followLead,
  loadFollowedLead,
  loadFollowedLeads,
  unfollowLead,
  signLead,
  loadLeadsToClose,
  loadLeadToClose,
  shareBill,
  loadDashboard,
  extractPublicLeadWithAI
};

export default features;